import React, { useCallback, useEffect, useMemo } from 'react'
import { hierarchyItems } from '../constants'
import { ScorecardMetricCR } from './ScorecardMetricCR'
import { GridSparkline } from './GridSparkline'
import { DifferenceCell } from './Renderers/DifferenceCell'
import { BBDTag, GraviGrid, Horizontal, TextCell, Texto, GraviButton } from '@gravitate-js/excalibrr'
import { defaultNumberDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { Popover, Tooltip } from 'antd'
import { useFormatter } from '@api/useFormatter'
import { useUserSettings } from '@hooks/settings'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'driver-analytics'

export const MetricGrid = ({
  gridData,
  gridAPIRef,
  comparisonAverages,
  comparisonKey,
  viewMetricBy,
  setSelectedRows,
  dataLoading,
}) => {
  const onRowSelected = useCallback((e) => {
    const selectedRows = e.api.getSelectedRows()
    if (selectedRows.length > 5) {
      e.node.setSelected(false)
    }
    setSelectedRows(e.api.getSelectedRows())
  }, [])

  const { formatVolume, formatDistance } = useFormatter()
  const formattedVolume = formatVolume(null, true)
  const formattedDistance = formatDistance(null, true)
  const { updateSetting } = useUserSettings()

  const columnDefs = useMemo(
    () => getColumnDefs(viewMetricBy, comparisonAverages, comparisonKey, formattedVolume, formattedDistance),
    [viewMetricBy, comparisonAverages, comparisonKey, formattedVolume, formattedDistance]
  )
  const getRowId = useCallback((row) => row.data?.id, [])
  const agPropOverrides = useMemo(
    () => ({
      frameworkComponents: {
        diffCell: DifferenceCell,
        sparkline: GridSparkline,
        texto: TextCell,
        score: ScorecardMetricCR,
      },
      onRowSelected: onRowSelected,
      rowGroupPanelShow: 'onlyWhenGrouping',
      rowMultiSelectWithClick: true,
      suppressCellSelection: true,
      rowSelection: 'multiple',
      getRowId,
    }),
    []
  )

  return (
    <GraviGrid
      externalRef={gridAPIRef}
      storageKey={STORAGE_KEY}
      agPropOverrides={agPropOverrides}
      columnDefs={columnDefs}
      rowData={gridData}
      loading={dataLoading}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      toolPanelWidth={260}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}

const getColumnDefs = (viewMetricBy, comparisonAverages, comparisonKey, formattedVolume, formattedDistance) => [
  {
    headerName: 'Driver Scorecard',
    groupId: 'driverScorecard',
    field: 'productivity',
    children: [
      {
        headerName: 'Market',
        field: 'market',
        valueGetter: (params) => {
          return params?.data?.market || params?.data?.id
        },
      },
      {
        headerName: 'Depot',
        field: 'depot_name',
        cellRenderer: (params) => {
          const depotName =
            params.data?.depot_name && typeof params.data?.depot_name !== 'string'
              ? params.data?.depot_name.join(', ')
              : params.data?.depot_name

          return (
            <Tooltip title={depotName} trigger={'hover'}>
              <GraviButton
                buttonText={<Texto className={'text-truncate'}>{depotName}</Texto>}
                style={{ backgroundColor: 'transparent', fontWeight: 400, maxWidth: '100%', boxShadow: 'none' }}
              />
            </Tooltip>
          )
        },
      },
      {
        headerName: 'Supervisor',
        field: 'supervisor_name',
        // hide: viewMetricBy !== 'supervisor',  // prevents sticky filter from working
      },
      {
        headerName: 'Driver',
        cellRendererParams: { weight: 'bold', category: 'h6' },
        minWidth: 160,
        cellRenderer: 'texto',
        field: 'name',
        // hide: viewMetricBy !== 'driver',   // prevents sticky filter from working
        valueGetter: (params) => (viewMetricBy === 'driver' ? params?.data?.name : null),
      },
      {
        headerName: 'Current Score',
        field: 'current_score',
        cellRenderer: 'score',
        maxWidth: 160,
        minWidth: 160,
        hide: true,
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'sparkline',
        valueGetter: (params) => params.data?.sparklineDifference,
        headerName: 'Score Trend',
        minWidth: 100,
        cellRenderer: 'sparkline',
        hide: true,
        filter: 'agNumberColumnFilter',
      },
      {
        headerName: 'Orders',
        cellRenderer: 'diffCell',
        cellRendererParams: {
          comparisonAverages: comparisonAverages,
          comparisonKey: comparisonKey,
          metricType: 'productivity',
          metricKey: 'orders',
        },
        field: 'productivity.orders',
        type: 'rightAligned',
        headerTooltip: 'Average Orders Per Shift',
        filter: 'agNumberColumnFilter',
        filterValueGetter: (params) => params.data?.productivity?.orders,
      },
      {
        headerName: 'Stops',
        cellRenderer: 'diffCell',
        headerTooltip: 'Average Orders Per Shift',
        cellRendererParams: {
          comparisonAverages: comparisonAverages,
          comparisonKey: comparisonKey,
          metricType: 'productivity',
          metricKey: 'stops',
        },
        field: 'productivity.stops',
        type: 'rightAligned',
        filter: 'agNumberColumnFilter',
        filterValueGetter: (params) => params.data?.productivity?.stops,
      },
      {
        headerName: `Total Distance (${formattedDistance})`,
        cellRenderer: 'diffCell',
        cellRendererParams: {
          comparisonAverages: comparisonAverages,
          comparisonKey: comparisonKey,
          metricType: 'productivity',
          metricKey: 'total_miles',
        },
        field: 'productivity.total_miles',
        type: 'rightAligned',
        filter: 'agNumberColumnFilter',
        filterValueGetter: (params) => params.data?.productivity?.total_miles,
      },
      {
        headerName: `Loaded Distance (${formattedDistance})`,
        cellRenderer: 'diffCell',
        cellRendererParams: {
          comparisonAverages: comparisonAverages,
          comparisonKey: comparisonKey,
          metricType: 'productivity',
          metricKey: 'loaded_miles',
        },
        field: 'productivity.loaded_miles',
        type: 'rightAligned',
        filter: 'agNumberColumnFilter',
        filterValueGetter: (params) => params.data?.productivity?.loaded_miles,
      },
      {
        headerName: 'Utilization %',
        cellRenderer: 'diffCell',
        cellRendererParams: {
          comparisonAverages: comparisonAverages,
          comparisonKey: comparisonKey,
          metricType: 'productivity',
          metricKey: 'utilization',
          isPercent: true,
        },
        field: 'productivity.utilization',
        type: 'rightAligned',
        filter: 'agNumberColumnFilter',
        filterValueGetter: (params) => params.data?.productivity?.utilization * 100,
      },
      // {
      //   headerName: 'Sick Days',
      //   cellRenderer: 'diffCell',
      //   cellRendererParams: {
      //     comparisonAverages: comparisonAverages,
      //     comparisonKey: comparisonKey,
      //     metricType: 'productivity',
      //     metricKey: 'sick_days',
      //   },
      //   field: 'productivity.sick_days',
      //   columnGroupShow: 'open',
      //   type: 'rightAligned',
      // },
      // {
      //   headerName: 'Total Miles Diff.',
      //   cellRenderer: 'diffCell',
      //   cellRendererParams: {
      //     comparisonAverages: comparisonAverages,
      //     comparisonKey: comparisonKey,
      //     metricType: 'productivity',
      //     metricKey: 'miles_est_act_diff',
      //     isPercent: true,
      //   },
      //   field: 'productivity.miles_est_act_diff',
      //   columnGroupShow: 'open',
      //   type: 'rightAligned',
      // },
      {
        headerName: 'Driving Time',
        cellRenderer: 'diffCell',
        cellRendererParams: {
          comparisonAverages: comparisonAverages,
          comparisonKey: comparisonKey,
          metricType: 'productivity',
          metricKey: 'driving_time',
        },
        field: 'productivity.driving_time',
        type: 'rightAligned',
        filter: 'agNumberColumnFilter',
        filterValueGetter: (params) => params.data?.productivity?.driving_time,
        hide: true,
      },
      // {
      //   headerName: 'Driving Time Diff',
      //   cellRenderer: 'diffCell',
      //   cellRendererParams: {
      //     comparisonAverages: comparisonAverages,
      //     comparisonKey: comparisonKey,
      //     metricType: 'productivity',
      //     metricKey: 'driving_time_diff',
      //     isPercent: true,
      //   },
      //   field: 'productivity.driving_time_diff',
      //   columnGroupShow: 'open',
      //   type: 'rightAligned',
      // },
      // {
      //   headerName: 'Loading Time',
      //   cellRenderer: 'diffCell',
      //   cellRendererParams: {
      //     comparisonAverages: comparisonAverages,
      //     comparisonKey: comparisonKey,
      //     metricType: 'productivity',
      //     metricKey: 'loading_time',
      //   },
      //   field: 'productivity.loading_time',
      //   type: 'rightAligned',
      //   hide: true,
      //   filter: 'agNumberColumnFilter',
      //   filterValueGetter: (params) => params.data?.productivity?.loading_time,
      // },
      // {
      //   headerName: 'Loading Time Diff.',
      //   cellRenderer: 'diffCell',
      //   cellRendererParams: {
      //     comparisonAverages: comparisonAverages,
      //     comparisonKey: comparisonKey,
      //     metricType: 'productivity',
      //     metricKey: 'loading_est_act_diff',
      //     isPercent: true,
      //   },
      //   field: 'productivity.loading_est_act_diff',
      //   columnGroupShow: 'open',
      //   type: 'rightAligned',
      // },
      // {
      //   headerName: 'Dropping Time',
      //   cellRenderer: 'diffCell',
      //   cellRendererParams: {
      //     comparisonAverages: comparisonAverages,
      //     comparisonKey: comparisonKey,
      //     metricType: 'productivity',
      //     metricKey: 'dropping_time',
      //   },
      //   field: 'productivity.dropping_time',
      //   type: 'rightAligned',
      //   hide: true,
      //   filter: 'agNumberColumnFilter',
      //   filterValueGetter: (params) => params.data?.productivity?.dropping_time,
      // },
      // {
      //   headerName: 'Dropping Time Diff',
      //   cellRenderer: 'diffCell',
      //   cellRendererParams: {
      //     comparisonAverages: comparisonAverages,
      //     comparisonKey: comparisonKey,
      //     metricType: 'productivity',
      //     metricKey: 'dropping_est_act_diff',
      //     isPercent: true,
      //   },
      //   field: 'productivity.dropping_est_act_diff',
      //   columnGroupShow: 'open',
      //   type: 'rightAligned',
      // },
      {
        headerName: `Loaded Volume (${formattedVolume})`,
        cellRenderer: 'diffCell',
        cellRendererParams: {
          comparisonAverages: comparisonAverages,
          comparisonKey: comparisonKey,
          metricType: 'productivity',
          metricKey: 'loaded_volume',
        },
        field: 'productivity.loaded_volume',
        type: 'rightAligned',
        headerTooltip: 'Average Gallons Per Shift',
        filter: 'agNumberColumnFilter',
        filterValueGetter: (params) => params.data?.productivity?.loaded_volume,
      },
    ],
  },
  {
    headerName: 'Accuracy',
    groupId: 'accuracy',
    field: 'accuracy',
    children: [
      {
        headerName: 'Mispulls',
        cellRenderer: 'diffCell',
        cellRendererParams: {
          comparisonAverages: comparisonAverages,
          comparisonKey: comparisonKey,
          metricType: 'accuracy',
          metricKey: 'mispulls',
        },
        field: 'accuracy.mispulls',
        type: 'rightAligned',
        filter: 'agNumberColumnFilter',
        filterValueGetter: (params) => params.data?.accuracy?.mispulls,
      },
      // {
      //   headerName: 'Misdrops',
      //   cellRenderer: 'diffCell',
      //   cellRendererParams: {
      //     comparisonAverages: comparisonAverages,
      //     comparisonKey: comparisonKey,
      //     metricType: 'accuracy',
      //     metricKey: 'misdrops',
      //   },
      //   field: 'accuracy.misdrops',
      //   columnGroupShow: 'open',
      //   type: 'rightAligned',
      // },
      // {
      //   headerName: '% Entry Errors',
      //   cellRenderer: 'diffCell',
      //   cellRendererParams: {
      //     comparisonAverages: comparisonAverages,
      //     comparisonKey: comparisonKey,
      //     metricType: 'accuracy',
      //     metricKey: 'data_input_accuracy',
      //     isPercent: true,
      //   },
      //   field: 'accuracy.data_input_accuracy',
      //   columnGroupShow: 'open',
      //   type: 'rightAligned',
      // },
      // {
      //   headerName: '% Gallons v. Standard',
      //   cellRenderer: 'diffCell',
      //   cellRendererParams: {
      //     comparisonAverages: comparisonAverages,
      //     comparisonKey: comparisonKey,
      //     metricType: 'accuracy',
      //     metricKey: 'loaded_vs_standard_gal',
      //     isPercent: true,
      //   },
      //   field: 'accuracy.loaded_vs_standard_gal',
      //   columnGroupShow: 'open',
      //   type: 'rightAligned',
      // },
    ],
  },
  // {
  //   headerName: 'Safety',
  //   children: [
  //     {
  //       headerName: 'Speeding Incidents',
  //       cellRenderer: 'diffCell',
  //       cellRendererParams: {
  //         comparisonAverages: comparisonAverages,
  //         comparisonKey: comparisonKey,
  //         metricType: 'safety',
  //         metricKey: 'speeding_incidents',
  //       },
  //       field: 'safety.speeding_incidents',
  //       columnGroupShow: 'closed',
  //       type: 'rightAligned',
  //     },
  //     {
  //       headerName: 'Harsh Braking',
  //       cellRenderer: 'diffCell',
  //       cellRendererParams: {
  //         comparisonAverages: comparisonAverages,
  //         comparisonKey: comparisonKey,
  //         metricType: 'safety',
  //         metricKey: 'harsh_braking',
  //       },
  //       field: 'safety.harsh_braking',
  //       columnGroupShow: 'open',
  //       type: 'rightAligned',
  //     },
  //     {
  //       headerName: 'Unsafe Distances',
  //       cellRenderer: 'diffCell',
  //       cellRendererParams: {
  //         comparisonAverages: comparisonAverages,
  //         comparisonKey: comparisonKey,
  //         metricType: 'safety',
  //         metricKey: 'unsafe_distances',
  //       },
  //       field: 'safety.unsafe_distances',
  //       columnGroupShow: 'open',
  //       type: 'rightAligned',
  //     },
  //     {
  //       headerName: 'Texting',
  //       cellRenderer: 'diffCell',
  //       cellRendererParams: {
  //         comparisonAverages: comparisonAverages,
  //         comparisonKey: comparisonKey,
  //         metricType: 'safety',
  //         metricKey: 'texting',
  //       },
  //       field: 'safety.texting',
  //       columnGroupShow: 'open',
  //       type: 'rightAligned',
  //     },
  //   ],
  // },
]
